import { useState } from 'react';

import styles from './RollingValue.module.css';

export interface RollingValueProps {
  /** What is shown, already formatted ("R$ 154,82"). */
  readonly text: string;
  /** The number behind it; the direction it moved decides whether digits roll up or down. */
  readonly value: number;
  readonly className?: string | undefined;
}

interface Shown {
  readonly text: string;
  readonly value: number;
  /**
   * Per character, aligned from the right: a counter that bumps (and so remounts it) when it
   * changes, and the direction it rolled in then. Kept per character so a later move the other way
   * does not restart the animation of a digit that did not change.
   */
  readonly chars: readonly {
    readonly generation: number;
    readonly direction: 'up' | 'down' | null;
  }[];
}

function nextChars(previous: Shown, text: string, direction: 'up' | 'down' | null): Shown['chars'] {
  return [...text].map((char, index) => {
    const old = previous.text.length - (text.length - index);
    const before = old >= 0 ? previous.chars[old] : undefined;
    if (before !== undefined && previous.text[old] === char) {
      return before;
    }
    return { generation: (before?.generation ?? 0) + 1, direction };
  });
}

/**
 * A total that follows a change instead of jumping: only the characters that changed roll in,
 * upward when the value went up and downward when it went down, in 220ms. A burst of changes
 * restarts the roll rather than queueing one per click. The whole text is also there for
 * assistive technology, read once; the rolling characters are hidden from it. Reduced motion:
 * the global rule makes the roll instant.
 */
export function RollingValue({ text, value, className }: RollingValueProps) {
  const [shown, setShown] = useState<Shown>(() => ({
    text,
    value,
    chars: [...text].map(() => ({ generation: 0, direction: null })),
  }));

  if (text !== shown.text) {
    const sign = Math.sign(value - shown.value);
    setShown({
      text,
      value,
      chars: nextChars(shown, text, sign > 0 ? 'up' : sign < 0 ? 'down' : null),
    });
  }

  const chars = [...shown.text];
  return (
    <span className={[styles.root, className].filter(Boolean).join(' ')}>
      <span className="visually-hidden">{shown.text}</span>
      <span className={styles.chars} aria-hidden="true">
        {chars.map((char, index) => {
          const fromRight = chars.length - index;
          const meta = shown.chars[index];
          return (
            <span
              key={`${String(fromRight)}-${String(meta?.generation ?? 0)}`}
              className={styles.char}
              data-roll={meta?.direction ?? undefined}
            >
              {char === ' ' ? ' ' : char}
            </span>
          );
        })}
      </span>
    </span>
  );
}
